import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Avatar,
  Divider,
  Chip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Chair as ChairIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import axios from "axios";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL;

const StudentProfile = ({ open, onClose, onProfileUpdate }) => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [formData, setFormData] = useState({
    phone_number: "",
    email: "",
    address: "",
    education: "",
  });
  const [errors, setErrors] = useState({});
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [previewModal, setPreviewModal] = useState({
    open: false,
    image: null,
    title: "",
  });

  // Fetch student data when modal opens
  React.useEffect(() => {
    if (open) {
      fetchStudentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchStudentData = async () => {
    setFetchingData(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.get(`${API}users/me/`, config);
      setStudent(response.data);
      setFormData({
        phone_number: response.data.phone_number || "",
        email: response.data.email || "",
        address: response.data.address || "",
        education: response.data.education || "",
      });
    } catch (err) {
      toast.error("Failed to fetch student data");
      onClose();
    } finally {
      setFetchingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        toast.error("Profile photo must be less than 2MB");
        e.target.value = null;
        return;
      }
      setProfilePhoto(file);
      setPreviewPhoto(URL.createObjectURL(file));
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setFormData({
      phone_number: student?.phone_number || "",
      email: student?.email || "",
      address: student?.address || "",
      education: student?.education || "",
    });
    setErrors({});
    setProfilePhoto(null);
    setPreviewPhoto(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      phone_number: student?.phone_number || "",
      email: student?.email || "",
      address: student?.address || "",
      education: student?.education || "",
    });
    setErrors({});
    setProfilePhoto(null);
    setPreviewPhoto(null);
  };

  const validateForm = () => {
    const newErrors = {};

    // Check for mandatory fields
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else {
      // Validate mobile number format (10 digits)
      const phoneRegex = /^[6-9]\d{9}$/;
      if (!phoneRegex.test(formData.phone_number.trim())) {
        newErrors.phone_number = "Please enter a valid 10-digit mobile number";
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.education.trim()) {
      newErrors.education = "Education is required";
    }

    if (!student.profile_photo && !profilePhoto) {
      newErrors.profile_photo = "Profile photo is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const updateData = new FormData();

      // Only add fields that have changed
      if (formData.phone_number !== student?.phone_number) {
        updateData.append("phone_number", formData.phone_number);
      }
      if (formData.email !== student?.email) {
        updateData.append("email", formData.email);
      }
      if (formData.address !== student?.address) {
        updateData.append("address", formData.address);
      }
      if (formData.education !== student?.education) {
        updateData.append("education", formData.education);
      }
      if (profilePhoto) {
        updateData.append("profile_photo", profilePhoto);
      }

      await axios.patch(`${API}users/update-profile/`, updateData, {
        ...config,
        headers: {
          ...config.headers,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Profile updated successfully!");
      setIsEditing(false);
      // Refresh the student data after update
      await fetchStudentData();
      onProfileUpdate?.(); // Callback to refresh parent data
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const getEducationLabel = (education) => {
    const educationMap = {
      ssc: "SSC",
      hsc: "HSC",
      diploma: "Diploma",
      degree: "Degree",
      masters: "Masters",
      phd: "PhD",
    };
    return educationMap[education] || education;
  };

  const getStatusColor = (status) => {
    return status === "Active" ? "success" : "error";
  };

  const handleUpdatePassword = () => {
    onClose(); // Close the profile modal
    navigate("/forgot-password");
  };

  const handlePhotoPreview = (image, title) => {
    setPreviewModal({
      open: true,
      image,
      title,
    });
  };

  const handleClosePreview = () => {
    setPreviewModal({
      open: false,
      image: null,
      title: "",
    });
  };

  // ✅ NEW: Safe ID Card field fallback (optional)
  const idCardPhotoUrl =
    student?.id_card_photo ||
    student?.id_card ||
    student?.id_card_image ||
    student?.id_card_url ||
    null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle className="bg-primaryColor text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PersonIcon />
              <Typography variant="h6" className="font-semibold">
                Student Profile
              </Typography>
            </div>
            {!isEditing && (
              <Button
                onClick={handleEdit}
                variant="outlined"
                sx={{ backgroundColor: "white", color: "primaryColor" }}
                className="text-white bg-white border-white hover:text-primaryColor font-medium px-2 md:px-4 py-2"
                startIcon={<EditIcon />}
              >
                <span className="hidden md:inline">Edit Profile</span>
              </Button>
            )}
          </div>
        </DialogTitle>

        <DialogContent className="p-6">
          {fetchingData ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <Typography variant="body1" className="text-gray-600">
                  Loading profile data...
                </Typography>
              </div>
            </div>
          ) : student ? (
            <div className="space-y-6 pt-3">
              {/* Profile Photo Section */}
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="flex flex-col items-center gap-3">
                  <Avatar
                    src={previewPhoto || student.profile_photo}
                    alt={`${student.first_name} ${student.last_name}`}
                    sx={{ width: 120, height: 120 }}
                    className="border-4 border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() =>
                      student.profile_photo &&
                      handlePhotoPreview(student.profile_photo, "Profile Photo")
                    }
                  />
                  {isEditing && (
                    <div>
                      <input
                        type="file"
                        accept="image/png,image/jpg,image/jpeg"
                        onChange={handlePhotoChange}
                        style={{ display: "none" }}
                        id="profile-photo-input"
                      />
                      {/* Choose Photo button intentionally removed */}
                      {errors.profile_photo && (
                        <Typography
                          variant="caption"
                          className="text-red-500 mt-1 block"
                        >
                          {errors.profile_photo}
                        </Typography>
                      )}
                    </div>
                  )}
                </div>

                <div className="text-center sm:text-left">
                  <Typography variant="h5" className="font-bold text-gray-800">
                    {student.first_name} {student.middle_name}{" "}
                    {student.last_name}
                  </Typography>
                  <Typography variant="body1" className="text-gray-600">
                    @{student.username}
                  </Typography>
                  <Chip
                    label={student.status}
                    color={getStatusColor(student.status)}
                    size="small"
                    className="mt-2"
                  />
                </div>
              </div>

              <Divider />

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Typography
                    variant="h6"
                    className="font-semibold text-gray-700 flex items-center gap-2"
                  >
                    <PhoneIcon className="text-blue-600" />
                    Contact Information
                  </Typography>

                  {isEditing ? (
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phone_number}
                      onChange={(e) =>
                        handleInputChange("phone_number", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      required
                      error={!!errors.phone_number}
                      helperText={
                        errors.phone_number || "Enter 10-digit mobile number"
                      }
                    />
                  ) : (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <Typography variant="body2" className="text-gray-600">
                        Phone Number
                      </Typography>
                      <Typography variant="body1" className="font-medium">
                        {student.phone_number}
                      </Typography>
                    </div>
                  )}

                  {isEditing ? (
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      required
                      error={!!errors.email}
                      helperText={errors.email || "Enter valid email address"}
                    />
                  ) : (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <Typography variant="body2" className="text-gray-600">
                        Email
                      </Typography>
                      <Typography variant="body1" className="font-medium">
                        {student.email}
                      </Typography>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <Typography
                    variant="h6"
                    className="font-semibold text-gray-700 flex items-center gap-2"
                  >
                    <LocationIcon className="text-green-600" />
                    Personal Information
                  </Typography>

                  {isEditing ? (
                    <TextField
                      fullWidth
                      label="Address"
                      value={formData.address}
                      onChange={(e) =>
                        handleInputChange("address", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      multiline
                      rows={2}
                      required
                      error={!!errors.address}
                      helperText={
                        errors.address || "Enter your complete address"
                      }
                    />
                  ) : (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <Typography variant="body2" className="text-gray-600">
                        Address
                      </Typography>
                      <Typography variant="body1" className="font-medium">
                        {student.address}
                      </Typography>
                    </div>
                  )}

                  {isEditing ? (
                    <TextField
                      fullWidth
                      label="Education"
                      value={formData.education}
                      onChange={(e) =>
                        handleInputChange("education", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      required
                      error={!!errors.education}
                      helperText={
                        errors.education || "Enter your education level"
                      }
                    />
                  ) : (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <Typography variant="body2" className="text-gray-600">
                        Education
                      </Typography>
                      <Typography variant="body1" className="font-medium">
                        {getEducationLabel(student.education)}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>

              <Divider />

              {/* Document Information */}
              <div>
                <Typography
                  variant="h6"
                  className="font-semibold text-gray-700 mb-4 flex items-center gap-2"
                >
                  <SchoolIcon className="text-purple-600" />
                  Documents
                </Typography>

                {student.aadhaar_front_photo ||
                student.aadhaar_back_photo ||
                idCardPhotoUrl ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Aadhaar Front */}
                    <div className="space-y-3">
                      <Typography
                        variant="subtitle2"
                        className="font-medium text-gray-700"
                      >
                        Aadhaar Front Photo
                      </Typography>
                      {student.aadhaar_front_photo ? (
                        <div className="relative">
                          <img
                            src={student.aadhaar_front_photo}
                            alt="Aadhaar Front"
                            className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() =>
                              handlePhotoPreview(
                                student.aadhaar_front_photo,
                                "Aadhaar Front Photo"
                              )
                            }
                          />
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                          <Typography variant="body2" className="text-gray-500">
                            No Aadhaar front photo uploaded
                          </Typography>
                        </div>
                      )}
                    </div>

                    {/* Aadhaar Back */}
                    <div className="space-y-3">
                      <Typography
                        variant="subtitle2"
                        className="font-medium text-gray-700"
                      >
                        Aadhaar Back Photo
                      </Typography>
                      {student.aadhaar_back_photo ? (
                        <div className="relative">
                          <img
                            src={student.aadhaar_back_photo}
                            alt="Aadhaar Back"
                            className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() =>
                              handlePhotoPreview(
                                student.aadhaar_back_photo,
                                "Aadhaar Back Photo"
                              )
                            }
                          />
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                          <Typography variant="body2" className="text-gray-500">
                            No Aadhaar back photo uploaded
                          </Typography>
                        </div>
                      )}
                    </div>

                    {/* ✅ NEW: ID Card (Optional) */}
                    <div className="space-y-3">
                      <Typography
                        variant="subtitle2"
                        className="font-medium text-gray-700"
                      >
                        ID Card Photo (Optional)
                      </Typography>
                      {idCardPhotoUrl ? (
                        <div className="relative">
                          <img
                            src={idCardPhotoUrl}
                            alt="ID Card"
                            className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() =>
                              handlePhotoPreview(idCardPhotoUrl, "ID Card Photo")
                            }
                          />
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                          <Typography variant="body2" className="text-gray-500">
                            No ID card photo uploaded
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                    <Typography
                      variant="body1"
                      className="text-blue-800 font-medium mb-2"
                    >
                      No Documents Available
                    </Typography>
                    <Typography variant="body2" className="text-blue-600">
                      Your documents will appear here after you provide them
                      during the booking process.
                    </Typography>
                  </div>
                )}
              </div>

              <Divider />

              {/* Security Section */}
              <div>
                <Typography
                  variant="h6"
                  className="font-semibold text-gray-700 mb-4 flex items-center gap-2"
                >
                  <LockIcon className="text-red-600" />
                  Security
                </Typography>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <Typography variant="body2" className="text-gray-600 mb-3">
                    Update your password to keep your account secure
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LockIcon />}
                    onClick={handleUpdatePassword}
                    className="border-red-500 text-red-600 hover:bg-red-50"
                  >
                    Update Password
                  </Button>
                </div>
              </div>

              <Divider />

              {/* Booking Information */}
              <div>
                <Typography
                  variant="h6"
                  className="font-semibold text-gray-700 mb-4 flex items-center gap-2"
                >
                  <SchoolIcon className="text-purple-600" />
                  Current Bookings
                </Typography>

                {/* Card width: full on mobile, half on larger screens */}
                <div className="flex justify-start">
                  <div className="w-full sm:w-1/2">
                    {/* Only Library Booking card kept */}
                    <div
                      className={`p-4 rounded-lg border-2 ${
                        student.library_seat
                          ? "border-green-200 bg-green-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <ChairIcon
                          className={
                            student.library_seat
                              ? "text-green-600"
                              : "text-gray-400"
                          }
                        />
                        <Typography
                          variant="subtitle1"
                          className="font-medium"
                        >
                          Library Booking
                        </Typography>
                      </div>
                      {student.library_seat ? (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <Typography
                              variant="body2"
                              className="text-gray-600"
                            >
                              Seat Number
                            </Typography>
                            <Typography
                              variant="body1"
                              className="font-semibold"
                            >
                              {student.library_seat}
                            </Typography>
                          </div>
                          <div className="flex justify-between items-center">
                            <Typography
                              variant="body2"
                              className="text-gray-600"
                            >
                              Booking ID
                            </Typography>
                            <Typography
                              variant="body1"
                              className="font-semibold"
                            >
                              #{student.library_booking_id}
                            </Typography>
                          </div>
                          <div className="flex justify-between items-center">
                            <Typography
                              variant="body2"
                              className="text-gray-600"
                            >
                              Status
                            </Typography>
                            <Chip
                              label={student.library_booking_status}
                              color={
                                student.library_booking_status === "approved"
                                  ? "success"
                                  : "warning"
                              }
                              size="small"
                              className="capitalize"
                            />
                          </div>


                          <div className="flex justify-between items-center">
                            <Typography
                              variant="body2"
                              className="text-gray-600"
                            >
                              Booking Start Date
                            </Typography>
                            <Typography
                              variant="body1"
                              className="font-semibold"
                            >
                              {student.library_start_date}
                            </Typography>
                          </div>

                          <div className="flex justify-between items-center">
                            <Typography
                              variant="body2"
                              className="text-gray-600"
                            >
                              Booking End Date
                            </Typography>
                            <Typography
                              variant="body1"
                              className="font-semibold"
                            >
                              {student.library_end_date}
                            </Typography>
                          </div>


                        </div>
                      ) : (
                        <Typography variant="body2" className="text-gray-500">
                          No library booking
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Typography variant="body1" className="text-center text-gray-500">
              No student data available
            </Typography>
          )}
        </DialogContent>

        <DialogActions className="p-6 pt-0">
          {isEditing ? (
            <>
              <Button
                onClick={handleCancel}
                variant="outlined"
                startIcon={<CancelIcon />}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </>
          ) : (
            <Button
              onClick={onClose}
              variant="contained"
              className="bg-gray-600 hover:bg-gray-700"
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Photo Preview Modal */}
      <Dialog
        open={previewModal.open}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle className="bg-gray-100">
          <div className="flex items-center justify-between">
            <Typography variant="h6" className="font-semibold">
              {previewModal.title}
            </Typography>
            <Button onClick={handleClosePreview} variant="outlined" size="small">
              Close
            </Button>
          </div>
        </DialogTitle>
        <DialogContent className="p-0">
          <div className="flex justify-center items-center p-4">
            <img
              src={previewModal.image}
              alt={previewModal.title}
              className="max-w-full max-h-[70vh] object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StudentProfile;
